<script setup>
import {
  reactive,
  computed,
  watch,
  ref,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import {
  required,
  requiredIf,
  minLength,
  maxLength,
} from '@vuelidate/validators';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useTrack } from 'dashboard/composables';
import { useCampaignWhatsappConnections } from 'dashboard/composables/useCampaignWhatsappConnections';

import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ComboBox from 'dashboard/components-next/combobox/ComboBox.vue';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import WhatsAppTemplateParser from 'dashboard/components-next/whatsapp/WhatsAppTemplateParser.vue';
import CampaignAiAgentFields from 'dashboard/components-next/Campaigns/Pages/CampaignPage/CampaignAiAgentFields.vue';
import AgentVoiceProfilesAPI from 'dashboard/api/agentVoiceProfiles';
import WhatsappCallsAPI from 'dashboard/api/whatsappCalls';
import { uploadFile } from 'dashboard/helper/uploadHelper';
import {
  buildAudienceOptions,
  toAudiencePayload,
  audienceListValue,
} from 'dashboard/helper/campaignAudience';

const props = defineProps({
  // When the form is opened from a "Público", the audience is pre-selected with
  // that list so the operator does not have to find it again in the dropdown.
  presetAudienceListId: {
    type: [Number, String],
    default: null,
  },
  campaignType: {
    type: String,
    required: true,
  },
});

const emit = defineEmits(['submit', 'cancel', 'back']);

const { t } = useI18n();
const store = useStore();

const SOURCE_CLOUD = 'cloud';
const SOURCE_WEB = 'web';
const CAMPAIGN_TYPE_MESSAGE = 'message';
const CAMPAIGN_TYPE_CALL = 'call';
const MAX_CALL_AUDIO_BYTES = 20 * 1024 * 1024;
const CALL_STATUS_MESSAGE_MAX_LENGTH = 1000;
const CALL_PROMPT_MAX_LENGTH = 4000;
const SUPPORTED_CALL_AUDIO_TYPES = [
  'audio/mpeg',
  'audio/wav',
  'audio/x-wav',
  'audio/ogg',
];
const CALL_STATUS_MESSAGE_KEYS = [
  'not_answered',
  'answered',
  'listened_full',
  'hung_up_early',
];
const DEFAULT_INTERVIEW_TIME = '10:00';

const formState = {
  uiFlags: useMapGetter('campaigns/getUIFlags'),
  labels: useMapGetter('labels/getLabels'),
  audienceLists: useMapGetter('audienceLists/getAudienceLists'),
  inboxes: useMapGetter('inboxes/getWhatsAppInboxes'),
  getFilteredWhatsAppTemplates: useMapGetter(
    'inboxes/getFilteredWhatsAppTemplates'
  ),
  captainAssistants: useMapGetter('captainAssistants/getRecords'),
};

const {
  connectionOptions,
  readyConnections,
  isLoading: isLoadingConnections,
  hasLoaded: hasLoadedConnections,
  fetchConnections,
  findConnection,
} = useCampaignWhatsappConnections();

const createCallStatusMessages = () =>
  Object.fromEntries(CALL_STATUS_MESSAGE_KEYS.map(status => [status, '']));

const buildInitialState = () => ({
  source: props.campaignType === CAMPAIGN_TYPE_CALL ? SOURCE_WEB : SOURCE_CLOUD,
  title: '',
  inboxId: null,
  connectionId: null,
  templateId: null,
  message: '',
  aiAgentEnabled: false,
  aiPrompt: '',
  captainAssistantId: null,
  voiceAgentId: null,
  callPrompt: '',
  testRecipientPhoneNumber: '',
  testRecipientConsent: false,
  callAudioBlobId: null,
  callStatusMessages: createCallStatusMessages(),
  dateVariationEnabled: false,
  dateVariationDays: [],
  dateVariationTime: DEFAULT_INTERVIEW_TIME,
  scheduledAt: null,
  selectedAudience: props.presetAudienceListId
    ? [audienceListValue(props.presetAudienceListId)]
    : [],
});

const state = reactive(buildInitialState());
const templateParserRef = ref(null);
const audioFileInput = ref(null);
const audioPreviewUrl = ref(null);
const audioState = ref('idle');
const audioError = ref('');
const mediaRecorder = ref(null);
const recorderStream = ref(null);
const recorderChunks = ref([]);
const isStartingTestCall = ref(false);
const testCallId = ref(null);
const testCallIdempotencyKey = ref(null);
const testCallStatus = ref('idle');
const testCallError = ref('');
const testCallPollingTimer = ref(null);
const voiceProfiles = ref([]);
const isLoadingVoiceProfiles = ref(false);
const hasLoadedVoiceProfiles = ref(false);
const voiceProfilesError = ref('');

const isCallCampaign = computed(
  () => props.campaignType === CAMPAIGN_TYPE_CALL
);
const isWebSource = computed(
  () => isCallCampaign.value || state.source === SOURCE_WEB
);
const isAiWriting = computed(
  () => !isCallCampaign.value && isWebSource.value && state.aiAgentEnabled
);

const rules = {
  title: { required, minLength: minLength(1) },
  inboxId: { required: requiredIf(() => !isWebSource.value) },
  connectionId: { required: requiredIf(() => isWebSource.value) },
  templateId: { required: requiredIf(() => !isWebSource.value) },
  message: {
    required: requiredIf(
      () => !isCallCampaign.value && isWebSource.value && !state.aiAgentEnabled
    ),
  },
  aiPrompt: { required: requiredIf(() => isAiWriting.value) },
  captainAssistantId: { required: requiredIf(() => isCallCampaign.value) },
  voiceAgentId: { required: requiredIf(() => isCallCampaign.value) },
  callPrompt: {
    required: requiredIf(() => isCallCampaign.value),
    maxLength: maxLength(CALL_PROMPT_MAX_LENGTH),
  },
  callAudioBlobId: {},
  callStatusMessages: {
    not_answered: { maxLength: maxLength(CALL_STATUS_MESSAGE_MAX_LENGTH) },
    answered: { maxLength: maxLength(CALL_STATUS_MESSAGE_MAX_LENGTH) },
    listened_full: { maxLength: maxLength(CALL_STATUS_MESSAGE_MAX_LENGTH) },
    hung_up_early: { maxLength: maxLength(CALL_STATUS_MESSAGE_MAX_LENGTH) },
  },
  dateVariationDays: { required: requiredIf(() => state.dateVariationEnabled) },
  dateVariationTime: { required: requiredIf(() => state.dateVariationEnabled) },
  scheduledAt: { required },
  selectedAudience: { required },
};

const v$ = useVuelidate(rules, state);

const isCreating = computed(() => formState.uiFlags.value.isCreating);

const currentDateTime = computed(() => {
  // Added to disable the scheduled at field from being set to the current time
  const now = new Date();
  const localTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return localTime.toISOString().slice(0, 16);
});

const mapToOptions = (items, valueKey, labelKey) =>
  items?.map(item => ({
    value: item[valueKey],
    label: item[labelKey],
  })) ?? [];

const selectedConnection = computed(() => findConnection(state.connectionId));
const selectedConnectionInboxId = computed(
  () => selectedConnection.value?.inbox?.id
);
const captainAssistantsForSelectedConnection = computed(() => {
  const assistants = formState.captainAssistants.value ?? [];
  const inboxId = selectedConnectionInboxId.value;

  if (!inboxId) return [];

  return assistants.filter(assistant => {
    const publicationMode = assistant.config?.publication_mode;
    if (publicationMode && publicationMode !== 'active') return false;
    if (!inboxId || !Array.isArray(assistant.inbox_ids)) return true;

    return assistant.inbox_ids.includes(inboxId);
  });
});
const captainAssistantOptions = computed(() =>
  mapToOptions(captainAssistantsForSelectedConnection.value, 'id', 'name')
);
const hasNoCaptainForSelectedConnection = computed(
  () =>
    Boolean(selectedConnectionInboxId.value) &&
    formState.captainAssistants.value?.length > 0 &&
    captainAssistantOptions.value.length === 0
);

const voiceAgentOptions = computed(() =>
  voiceProfiles.value.map(profile => ({
    value: profile.agent_id,
    label: profile.email ? `${profile.name} (${profile.email})` : profile.name,
  }))
);
const hasNoReadyVoiceProfiles = computed(
  () =>
    hasLoadedVoiceProfiles.value &&
    !isLoadingVoiceProfiles.value &&
    !voiceProfilesError.value &&
    voiceAgentOptions.value.length === 0
);

const sourceOptions = computed(() => [
  {
    value: SOURCE_CLOUD,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SOURCE.CLOUD.LABEL'),
    description: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SOURCE.CLOUD.DESCRIPTION'),
  },
  {
    value: SOURCE_WEB,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SOURCE.WEB.LABEL'),
    description: t('CAMPAIGN.WHATSAPP.CREATE.FORM.SOURCE.WEB.DESCRIPTION'),
  },
]);

const dateVariationDayOptions = computed(() => [
  {
    value: 1,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.MONDAY'),
  },
  {
    value: 2,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.TUESDAY'),
  },
  {
    value: 3,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.WEDNESDAY'),
  },
  {
    value: 4,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.THURSDAY'),
  },
  {
    value: 5,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.FRIDAY'),
  },
  {
    value: 6,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.SATURDAY'),
  },
  {
    value: 0,
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.SUNDAY'),
  },
]);

const dateVariationTokens = computed(() => [
  {
    label: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TOKENS.CONTACT_NAME'
    ),
    value: '{{ contato.nome }}',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TOKENS.DATE'),
    value: '{{ interview.proxima_data }}',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TOKENS.TIME'),
    value: '{{ interview.proxima_horario }}',
  },
  {
    label: t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TOKENS.DATETIME'),
    value: '{{ interview.proxima_data_hora_extenso }}',
  },
]);

const audienceList = computed(() =>
  buildAudienceOptions({
    labels: formState.labels.value,
    audienceLists: formState.audienceLists.value,
    listPrefixLabel: t('CAMPAIGN.AUDIENCE.OPTION_PREFIX'),
  })
);

// Only WhatsApp Cloud inboxes can run template campaigns —
// Whatsapp::OneoffCampaignService hard-rejects any other provider at trigger
// time, so offering them here only produced campaigns guaranteed to fail.
const inboxOptions = computed(() =>
  mapToOptions(
    (formState.inboxes.value ?? []).filter(
      inbox => inbox.provider === 'whatsapp_cloud'
    ),
    'id',
    'name'
  )
);

const hasNoCloudInbox = computed(() => inboxOptions.value.length === 0);
const hasNoConnection = computed(
  () => hasLoadedConnections.value && readyConnections.value.length === 0
);

const templateOptions = computed(() => {
  if (!state.inboxId) return [];
  const templates = formState.getFilteredWhatsAppTemplates.value(state.inboxId);
  return templates.map(template => {
    // Create a more user-friendly label from template name
    const friendlyName = template.name
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    return {
      value: template.id,
      label: `${friendlyName} (${template.language || 'en'})`,
      template: template,
    };
  });
});

const selectedTemplate = computed(() => {
  if (!state.templateId) return null;
  return templateOptions.value.find(option => option.value === state.templateId)
    ?.template;
});

const callStatusMessageOptions = computed(() => [
  {
    key: 'not_answered',
    label: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_STATUS_MESSAGES.NOT_ANSWERED.LABEL'
    ),
    placeholder: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_STATUS_MESSAGES.NOT_ANSWERED.PLACEHOLDER'
    ),
  },
  {
    key: 'answered',
    label: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_STATUS_MESSAGES.ANSWERED.LABEL'
    ),
    placeholder: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_STATUS_MESSAGES.ANSWERED.PLACEHOLDER'
    ),
  },
  {
    key: 'listened_full',
    label: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_STATUS_MESSAGES.LISTENED_FULL.LABEL'
    ),
    placeholder: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_STATUS_MESSAGES.LISTENED_FULL.PLACEHOLDER'
    ),
  },
  {
    key: 'hung_up_early',
    label: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_STATUS_MESSAGES.HUNG_UP_EARLY.LABEL'
    ),
    placeholder: t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_STATUS_MESSAGES.HUNG_UP_EARLY.PLACEHOLDER'
    ),
  },
]);

const getErrorMessage = (field, errorKey) => {
  const baseKey = 'CAMPAIGN.WHATSAPP.CREATE.FORM';
  return v$.value[field].$error ? t(`${baseKey}.${errorKey}.ERROR`) : '';
};

const formErrors = computed(() => ({
  title: getErrorMessage('title', 'TITLE'),
  inbox: getErrorMessage('inboxId', 'INBOX'),
  connection: getErrorMessage('connectionId', 'CONNECTION'),
  template: getErrorMessage('templateId', 'TEMPLATE'),
  message: getErrorMessage('message', 'MESSAGE'),
  callAudio:
    getErrorMessage('callAudioBlobId', 'CALL_AUDIO') || audioError.value,
  captainAssistant: getErrorMessage('captainAssistantId', 'CALL_CAPTAIN'),
  voiceAgent: getErrorMessage('voiceAgentId', 'CALL_VOICE_AGENT'),
  callPrompt: getErrorMessage('callPrompt', 'CALL_PROMPT'),
  callStatusMessages: {
    not_answered: v$.value.callStatusMessages.not_answered.$error
      ? t(
          'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_STATUS_MESSAGES.NOT_ANSWERED.ERROR'
        )
      : '',
    answered: v$.value.callStatusMessages.answered.$error
      ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_STATUS_MESSAGES.ANSWERED.ERROR')
      : '',
    listened_full: v$.value.callStatusMessages.listened_full.$error
      ? t(
          'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_STATUS_MESSAGES.LISTENED_FULL.ERROR'
        )
      : '',
    hung_up_early: v$.value.callStatusMessages.hung_up_early.$error
      ? t(
          'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_STATUS_MESSAGES.HUNG_UP_EARLY.ERROR'
        )
      : '',
  },
  dateVariationDays: getErrorMessage(
    'dateVariationDays',
    'DATE_VARIATION.DAYS'
  ),
  dateVariationTime: getErrorMessage(
    'dateVariationTime',
    'DATE_VARIATION.TIME'
  ),
  scheduledAt: getErrorMessage('scheduledAt', 'SCHEDULED_AT'),
  audience: getErrorMessage('selectedAudience', 'AUDIENCE'),
  // Required once the agent is on. Without surfacing it the submit button was
  // simply disabled with nothing on screen to explain why.
  aiPrompt: v$.value.aiPrompt.$error ? t('CAMPAIGN.AI_AGENT.PROMPT.ERROR') : '',
}));

// The previous expression (`... === false || true`) was constant-true, so a
// template with unfilled {{1}} variables could be submitted and the campaign
// would go out with empty parameters. Only require the parser to be valid when
// a template that actually has variables/media is selected.
const hasRequiredTemplateParams = computed(() => {
  if (isWebSource.value || isCallCampaign.value) return true;

  const parser = templateParserRef.value;
  if (!parser) return true;
  if (!parser.hasVariables && !parser.hasMediaHeader) return true;

  return parser.v$?.$invalid === false;
});

const isSubmitDisabled = computed(
  () =>
    v$.value.$invalid ||
    !hasRequiredTemplateParams.value ||
    (isCallCampaign.value &&
      (isLoadingVoiceProfiles.value || Boolean(voiceProfilesError.value))) ||
    ['recording', 'uploading'].includes(audioState.value)
);

const isTestRecipientPhoneNumberValid = computed(() =>
  /^\+[1-9]\d{7,14}$/.test(state.testRecipientPhoneNumber.trim())
);
const terminalTestCallStatuses = new Set(['ended', 'failed', 'rejected']);
const isTestCallRunning = computed(
  () =>
    Boolean(testCallId.value) &&
    !terminalTestCallStatuses.has(testCallStatus.value)
);
const isTestCallReady = computed(
  () =>
    Boolean(
      selectedConnection.value?.active &&
        state.captainAssistantId &&
        state.voiceAgentId &&
        !isLoadingVoiceProfiles.value &&
        !voiceProfilesError.value &&
        state.callPrompt.trim() &&
        state.testRecipientConsent &&
        isTestRecipientPhoneNumberValid.value
    ) &&
    !isStartingTestCall.value &&
    !isTestCallRunning.value
);
const testCallStatusLabel = computed(() =>
  t(
    `CAMPAIGN.WHATSAPP.CREATE.FORM.TEST_CALL.STATUS.${testCallStatus.value.toUpperCase()}`
  )
);

const formatToUTCString = localDateTime =>
  localDateTime ? new Date(localDateTime).toISOString() : null;

const browserTimezone = () =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

const parseDateVariationTime = () => {
  const match = state.dateVariationTime?.match(/^([01]\d|2[0-3]):([0-5]\d)$/);
  if (!match) return null;

  return { hour: Number(match[1]), minute: Number(match[2]) };
};

function dateVariationRules() {
  if (!state.dateVariationEnabled) return {};

  return {
    enabled: true,
    days_of_week: [...new Set(state.dateVariationDays.map(Number))].sort(
      (left, right) => left - right
    ),
    time: state.dateVariationTime,
    timezone: browserTimezone(),
  };
}

const aiPreviewContext = computed(() => ({
  title: state.title,
  message: state.message,
  lais_whatsapp_connection_id: state.connectionId,
  audience: toAudiencePayload(state.selectedAudience),
  date_variation_rules: dateVariationRules(),
  sample_size: 2,
}));

const formatDateVariationTime = date => {
  const hour = date.getHours();
  const minute = date.getMinutes();
  return minute === 0
    ? `${hour}h`
    : `${hour}h${String(minute).padStart(2, '0')}`;
};

const dateVariationPeriod = hour => {
  if (hour < 6)
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.PERIODS.DAWN');
  if (hour < 12)
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.PERIODS.MORNING');
  if (hour < 18)
    return t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.PERIODS.AFTERNOON');

  return t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.PERIODS.NIGHT');
};

const nextDateVariationOccurrence = computed(() => {
  if (!state.dateVariationEnabled || state.dateVariationDays.length === 0) {
    return null;
  }

  const time = parseDateVariationTime();
  if (!time) return null;

  const now = new Date();
  const selectedDays = state.dateVariationDays.map(Number);
  for (let offset = 0; offset <= 7; offset += 1) {
    const candidate = new Date(now);
    candidate.setDate(now.getDate() + offset);
    candidate.setHours(time.hour, time.minute, 0, 0);
    if (selectedDays.includes(candidate.getDay()) && candidate >= now) {
      return candidate;
    }
  }

  return null;
});

const nextDateVariationPreview = computed(() => {
  const occurrence = nextDateVariationOccurrence.value;
  if (!occurrence) return '';

  const date = new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(occurrence);
  const time = formatDateVariationTime(occurrence);

  return `${date} - ${time} ${dateVariationPeriod(occurrence.getHours())}`;
});

const toggleDateVariationDay = dayValue => {
  state.dateVariationDays = state.dateVariationDays.includes(dayValue)
    ? state.dateVariationDays.filter(value => value !== dayValue)
    : [...state.dateVariationDays, dayValue];
};

const fetchReadyVoiceProfiles = async () => {
  isLoadingVoiceProfiles.value = true;
  voiceProfilesError.value = '';

  try {
    const { data } = await AgentVoiceProfilesAPI.listReady();
    voiceProfiles.value = data?.data ?? [];
    if (
      state.voiceAgentId &&
      !voiceProfiles.value.some(
        profile => String(profile.agent_id) === String(state.voiceAgentId)
      )
    ) {
      state.voiceAgentId = null;
    }
  } catch (error) {
    voiceProfiles.value = [];
    state.voiceAgentId = null;
    voiceProfilesError.value = t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_VOICE_AGENT.LOAD_ERROR'
    );
  } finally {
    isLoadingVoiceProfiles.value = false;
    hasLoadedVoiceProfiles.value = true;
  }
};

const clearTestCallPolling = () => {
  if (testCallPollingTimer.value) {
    window.clearTimeout(testCallPollingTimer.value);
    testCallPollingTimer.value = null;
  }
};

const createTestCallIdempotencyKey = () => {
  const requestId = window.crypto?.randomUUID?.();
  const fallbackId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `whatsapp-call-test:${requestId || fallbackId}`;
};

const testCallPayload = () => ({
  lais_whatsapp_connection_id: state.connectionId,
  captain_assistant_id: state.captainAssistantId,
  voice_agent_id: state.voiceAgentId,
  call_prompt: state.callPrompt.trim(),
  date_variation_rules: dateVariationRules(),
  recipient_phone_number: state.testRecipientPhoneNumber.trim(),
  test_recipient_consent: { attested: state.testRecipientConsent },
  ...(state.callAudioBlobId
    ? { call_audio_blob_id: state.callAudioBlobId }
    : {}),
});

const scheduleTestCallRefresh = refresh => {
  clearTestCallPolling();
  if (!testCallId.value || terminalTestCallStatuses.has(testCallStatus.value)) {
    return;
  }

  testCallPollingTimer.value = window.setTimeout(refresh, 2000);
};

async function refreshTestCallStatus() {
  if (!testCallId.value) return;

  try {
    const { data } = await WhatsappCallsAPI.getCall(testCallId.value);
    const call = data?.call ?? data;
    testCallStatus.value = call?.status || testCallStatus.value;
    if (terminalTestCallStatuses.has(testCallStatus.value)) {
      testCallIdempotencyKey.value = null;
    }
    scheduleTestCallRefresh(refreshTestCallStatus);
  } catch (error) {
    clearTestCallPolling();
    testCallError.value = t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.TEST_CALL.ERROR.STATUS'
    );
  }
}

const startTestCall = async () => {
  if (!isTestCallReady.value) return;

  isStartingTestCall.value = true;
  testCallError.value = '';
  testCallStatus.value = 'starting';
  try {
    testCallIdempotencyKey.value ||= createTestCallIdempotencyKey();
    const { data } = await WhatsappCallsAPI.startTest(
      testCallPayload(),
      testCallIdempotencyKey.value
    );
    const call = data?.call ?? data;
    testCallId.value = call?.id || data?.call_id;
    testCallStatus.value = call?.status || data?.status || 'ringing';
    if (!testCallId.value) throw new Error('test call id is missing');

    useTrack('Started WhatsApp call campaign test', {
      connection_id: state.connectionId,
      captain_assistant_id: state.captainAssistantId,
      voice_agent_id: state.voiceAgentId,
    });
    scheduleTestCallRefresh(refreshTestCallStatus);
  } catch (error) {
    clearTestCallPolling();
    testCallStatus.value = 'failed';
    testCallError.value =
      error?.response?.data?.error ||
      t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEST_CALL.ERROR.START');
  } finally {
    isStartingTestCall.value = false;
  }
};

const handleCancel = () => emit('cancel');
const handleBack = () => emit('back');

const prepareTemplateCampaign = () => {
  // Find the selected template to get its content
  const currentTemplate = selectedTemplate.value;
  const parserData = templateParserRef.value;

  // Extract template content - this should be the template message body
  const templateContent = parserData?.renderedTemplate || '';

  // Prepare template_params object with the same structure as used in contacts
  const templateParams = {
    name: currentTemplate?.name || '',
    namespace: currentTemplate?.namespace || '',
    category: currentTemplate?.category || 'UTILITY',
    language: currentTemplate?.language || 'en_US',
    processed_params: parserData?.processedParams || {},
  };

  return {
    whatsapp_campaign_type: CAMPAIGN_TYPE_MESSAGE,
    message: templateContent,
    template_params: templateParams,
    inbox_id: state.inboxId,
  };
};

// A WhatsApp Web campaign sends free text from the connected phone, so it
// carries no template at all — and can hand the body to the AI agent.
const prepareWebCampaign = () => ({
  whatsapp_campaign_type: CAMPAIGN_TYPE_MESSAGE,
  message: state.aiAgentEnabled ? '' : state.message,
  lais_whatsapp_connection_id: state.connectionId,
  ai_agent_enabled: state.aiAgentEnabled,
  ai_prompt: state.aiAgentEnabled ? state.aiPrompt : null,
});

const prepareCallStatusMessages = () =>
  Object.fromEntries(
    CALL_STATUS_MESSAGE_KEYS.map(status => [
      status,
      state.callStatusMessages[status].trim(),
    ]).filter(([, message]) => Boolean(message))
  );

const prepareCallCampaign = () => ({
  whatsapp_campaign_type: CAMPAIGN_TYPE_CALL,
  message: '',
  sender_id: state.voiceAgentId,
  lais_whatsapp_connection_id: state.connectionId,
  captain_assistant_id: state.captainAssistantId,
  voice_agent_id: state.voiceAgentId,
  call_prompt: state.callPrompt.trim(),
  ...(state.callAudioBlobId
    ? { call_audio_blob_id: state.callAudioBlobId }
    : {}),
  call_status_messages: prepareCallStatusMessages(),
});

const campaignDetailsForSelectedType = () => {
  if (isCallCampaign.value) return prepareCallCampaign();
  if (isWebSource.value) return prepareWebCampaign();

  return prepareTemplateCampaign();
};

const prepareCampaignDetails = () => ({
  title: state.title,
  scheduled_at: formatToUTCString(state.scheduledAt),
  audience: toAudiencePayload(state.selectedAudience),
  date_variation_rules: dateVariationRules(),
  ...campaignDetailsForSelectedType(),
});

const isSupportedCallAudio = file =>
  SUPPORTED_CALL_AUDIO_TYPES.some(type =>
    file.type.toLowerCase().startsWith(type)
  );

const clearPreview = () => {
  if (audioPreviewUrl.value) URL.revokeObjectURL(audioPreviewUrl.value);
  audioPreviewUrl.value = null;
};

const removeCallAudio = () => {
  clearPreview();
  state.callAudioBlobId = null;
  audioError.value = '';
  if (audioState.value !== 'recording') audioState.value = 'idle';
};

const uploadCallAudio = async (file, source) => {
  audioState.value = 'uploading';
  audioError.value = '';
  try {
    const { blobId } = await uploadFile(file);
    state.callAudioBlobId = blobId;
    audioState.value = 'ready';
    useTrack('Uploaded WhatsApp campaign call audio', {
      source,
      content_type: file.type,
      byte_size: file.size,
    });
  } catch (error) {
    state.callAudioBlobId = null;
    audioState.value = 'error';
    audioError.value = t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_AUDIO.ERROR.UPLOAD'
    );
  }
};

const selectCallAudio = async (file, source = 'import') => {
  if (!file) return;
  if (!isSupportedCallAudio(file)) {
    audioError.value = t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_AUDIO.ERROR.TYPE');
    return;
  }
  if (file.size > MAX_CALL_AUDIO_BYTES) {
    audioError.value = t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_AUDIO.ERROR.SIZE');
    return;
  }

  removeCallAudio();
  audioPreviewUrl.value = URL.createObjectURL(file);
  await uploadCallAudio(file, source);
};

const handleAudioFileChange = event => {
  const [file] = event.target.files || [];
  event.target.value = '';
  selectCallAudio(file);
};

const selectedRecorderMimeType = () =>
  ['audio/ogg;codecs=opus', 'audio/ogg'].find(type =>
    MediaRecorder.isTypeSupported(type)
  );

const startRecording = async () => {
  if (!window.MediaRecorder || !navigator.mediaDevices?.getUserMedia) {
    audioError.value = t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_AUDIO.ERROR.RECORDING_UNSUPPORTED'
    );
    return;
  }

  const mimeType = selectedRecorderMimeType();
  if (!mimeType) {
    audioError.value = t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_AUDIO.ERROR.RECORDING_UNSUPPORTED'
    );
    return;
  }

  try {
    recorderStream.value = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    recorderChunks.value = [];
    mediaRecorder.value = new MediaRecorder(recorderStream.value, { mimeType });
    mediaRecorder.value.ondataavailable = event =>
      recorderChunks.value.push(event.data);
    mediaRecorder.value.onstop = async () => {
      recorderStream.value?.getTracks().forEach(track => track.stop());
      const recording = new File(
        recorderChunks.value,
        `whatsapp-call-${Date.now()}.ogg`,
        { type: mimeType }
      );
      mediaRecorder.value = null;
      await selectCallAudio(recording, 'recording');
    };
    mediaRecorder.value.start();
    audioState.value = 'recording';
    audioError.value = '';
    useTrack('Started WhatsApp campaign call recording', {
      campaign_type: CAMPAIGN_TYPE_CALL,
    });
  } catch (error) {
    recorderStream.value?.getTracks().forEach(track => track.stop());
    audioState.value = 'error';
    audioError.value = t(
      'CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_AUDIO.ERROR.MICROPHONE'
    );
  }
};

const stopRecording = () => {
  mediaRecorder.value?.stop();
  useTrack('Stopped WhatsApp campaign call recording', {
    campaign_type: CAMPAIGN_TYPE_CALL,
  });
};

const handleSubmit = async () => {
  const isFormValid = await v$.value.$validate();
  if (!isFormValid) return;

  emit('submit', prepareCampaignDetails());
};

// Reset template selection when inbox changes
watch(
  () => state.inboxId,
  () => {
    state.templateId = null;
  }
);

watch(selectedConnectionInboxId, () => {
  if (
    state.captainAssistantId &&
    !captainAssistantOptions.value.some(
      option => option.value === state.captainAssistantId
    )
  ) {
    state.captainAssistantId = null;
  }
});

// An account with no Cloud API inbox can only dispatch over WhatsApp Web, so
// land on the source that actually works instead of an empty template picker.
// Only applies before the operator touches the control.
const sourceTouched = ref(false);
watch(
  () => state.source,
  () => {
    sourceTouched.value = true;
  }
);

watch([hasNoCloudInbox, hasNoConnection], ([noCloud, noConnection]) => {
  if (sourceTouched.value) return;
  if (noCloud && !noConnection) state.source = SOURCE_WEB;
});

onMounted(() => {
  fetchConnections();
  store.dispatch('captainAssistants/get', { page: 1, perPage: 100 });
  if (isCallCampaign.value) fetchReadyVoiceProfiles();
});
onBeforeUnmount(() => {
  clearTestCallPolling();
  if (mediaRecorder.value && mediaRecorder.value.state !== 'inactive')
    mediaRecorder.value.stop();
  recorderStream.value?.getTracks().forEach(track => track.stop());
  clearPreview();
});
</script>

<template>
  <form class="flex flex-col gap-4" @submit.prevent="handleSubmit">
    <Input
      v-model="state.title"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.LABEL')"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TITLE.PLACEHOLDER')"
      :message="formErrors.title"
      :message-type="formErrors.title ? 'error' : 'info'"
    />

    <fieldset
      v-if="!isCallCampaign"
      class="flex flex-col gap-1 p-0 m-0 border-0"
    >
      <legend class="mb-1.5 p-0 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.SOURCE.LABEL') }}
      </legend>
      <div class="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <label
          v-for="option in sourceOptions"
          :key="option.value"
          class="flex flex-col gap-1 p-3 transition-colors border rounded-lg cursor-pointer"
          :class="
            state.source === option.value
              ? 'border-n-brand bg-n-alpha-2'
              : 'border-n-weak hover:bg-n-alpha-1'
          "
        >
          <span class="flex items-center gap-2">
            <input
              v-model="state.source"
              type="radio"
              :value="option.value"
              class="size-4 accent-n-blue-9"
            />
            <span class="text-sm font-medium text-n-slate-12">
              {{ option.label }}
            </span>
          </span>
          <span class="text-xs text-n-slate-11">{{ option.description }}</span>
        </label>
      </div>
    </fieldset>

    <!-- Cloud API: approved template -->
    <template v-if="!isWebSource">
      <div class="flex flex-col gap-1">
        <label for="inbox" class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.LABEL') }}
        </label>
        <ComboBox
          id="inbox"
          v-model="state.inboxId"
          :options="inboxOptions"
          :has-error="!!formErrors.inbox"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.PLACEHOLDER')"
          :message="formErrors.inbox"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
        <p v-if="hasNoCloudInbox" class="mt-1 text-xs text-n-amber-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.INBOX.EMPTY') }}
        </p>
      </div>

      <div class="flex flex-col gap-1">
        <label
          for="template"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.LABEL') }}
        </label>
        <ComboBox
          id="template"
          v-model="state.templateId"
          :options="templateOptions"
          :has-error="!!formErrors.template"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.PLACEHOLDER')"
          :message="formErrors.template"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
        <p class="mt-1 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEMPLATE.INFO') }}
        </p>
      </div>

      <!-- Template Parser -->
      <WhatsAppTemplateParser
        v-if="selectedTemplate"
        ref="templateParserRef"
        :template="selectedTemplate"
      />
    </template>

    <!-- WhatsApp Web: free text from a connected phone -->
    <template v-else>
      <div class="flex flex-col gap-1">
        <label
          for="connection"
          class="mb-0.5 text-sm font-medium text-n-slate-12"
        >
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONNECTION.LABEL') }}
        </label>
        <ComboBox
          id="connection"
          v-model="state.connectionId"
          :options="connectionOptions"
          :has-error="!!formErrors.connection"
          :placeholder="
            isLoadingConnections
              ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONNECTION.LOADING')
              : t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONNECTION.PLACEHOLDER')
          "
          :message="formErrors.connection"
          class="[&>div>button]:bg-n-alpha-black2 [&>div>button:not(.focused)]:dark:outline-n-weak [&>div>button:not(.focused)]:hover:!outline-n-slate-6"
        />
        <p v-if="hasNoConnection" class="mt-1 text-xs text-n-amber-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONNECTION.EMPTY') }}
        </p>
        <p v-else class="mt-1 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CONNECTION.INFO') }}
        </p>
      </div>

      <template v-if="isCallCampaign">
        <div
          class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
        >
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_CAPTAIN.LABEL') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_CAPTAIN.INFO') }}
            </p>
          </div>
          <ComboBox
            id="call-captain"
            v-model="state.captainAssistantId"
            :options="captainAssistantOptions"
            :has-error="!!formErrors.captainAssistant"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_CAPTAIN.PLACEHOLDER')
            "
            :message="formErrors.captainAssistant"
          />
          <p
            v-if="hasNoCaptainForSelectedConnection"
            class="text-xs text-n-amber-11"
          >
            {{
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_CAPTAIN.NO_MATCHING_INBOX')
            }}
          </p>
          <label
            for="call-voice-agent"
            class="mb-0.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_VOICE_AGENT.LABEL') }}
          </label>
          <ComboBox
            id="call-voice-agent"
            v-model="state.voiceAgentId"
            :options="voiceAgentOptions"
            :has-error="!!formErrors.voiceAgent"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_VOICE_AGENT.PLACEHOLDER')
            "
            :message="formErrors.voiceAgent"
          />
          <p v-if="isLoadingVoiceProfiles" class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_VOICE_AGENT.LOADING') }}
          </p>
          <p
            v-else-if="hasNoReadyVoiceProfiles"
            class="text-xs text-n-amber-11"
          >
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_VOICE_AGENT.EMPTY') }}
          </p>
          <div
            v-else-if="voiceProfilesError"
            class="flex flex-wrap items-center gap-2"
          >
            <p class="text-xs text-n-ruby-11">{{ voiceProfilesError }}</p>
            <Button
              type="button"
              variant="faded"
              color="slate"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_VOICE_AGENT.RETRY')"
              @click="fetchReadyVoiceProfiles"
            />
          </div>
          <TextArea
            id="call-prompt"
            v-model="state.callPrompt"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_PROMPT.LABEL')"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_PROMPT.PLACEHOLDER')
            "
            :max-length="CALL_PROMPT_MAX_LENGTH"
            show-character-count
            :message="formErrors.callPrompt"
            :message-type="formErrors.callPrompt ? 'error' : 'info'"
          />
        </div>
        <div
          class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
        >
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEST_CALL.LABEL') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEST_CALL.INFO') }}
            </p>
          </div>
          <Input
            v-model="state.testRecipientPhoneNumber"
            type="tel"
            autocomplete="tel"
            :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEST_CALL.PHONE_LABEL')"
            :placeholder="
              t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEST_CALL.PHONE_PLACEHOLDER')
            "
            :message="
              state.testRecipientPhoneNumber && !isTestRecipientPhoneNumberValid
                ? t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEST_CALL.PHONE_ERROR')
                : ''
            "
            :message-type="
              state.testRecipientPhoneNumber && !isTestRecipientPhoneNumberValid
                ? 'error'
                : 'info'
            "
          />
          <label class="flex items-start gap-2 text-xs text-n-slate-11">
            <input
              v-model="state.testRecipientConsent"
              type="checkbox"
              class="mt-0.5 rounded border-n-weak"
            />
            <span>
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEST_CALL.CONSENT') }}
            </span>
          </label>
          <div class="flex flex-wrap items-center gap-2">
            <Button
              type="button"
              variant="faded"
              color="slate"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEST_CALL.BUTTON')"
              :is-loading="isStartingTestCall"
              :disabled="!isTestCallReady"
              @click="startTestCall"
            />
            <p
              v-if="testCallStatus !== 'idle'"
              class="text-xs font-medium text-n-slate-11"
            >
              {{
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.TEST_CALL.STATUS_LABEL', {
                  status: testCallStatusLabel,
                })
              }}
            </p>
          </div>
          <p v-if="testCallError" class="text-xs text-n-ruby-11">
            {{ testCallError }}
          </p>
        </div>
        <div
          class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
        >
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_AUDIO.LABEL') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_AUDIO.INFO') }}
            </p>
          </div>
          <input
            ref="audioFileInput"
            type="file"
            accept="audio/mpeg,audio/wav,audio/x-wav,audio/ogg,.mp3,.wav,.ogg"
            class="sr-only"
            @change="handleAudioFileChange"
          />
          <div class="flex flex-wrap gap-2">
            <Button
              v-if="audioState !== 'recording'"
              type="button"
              variant="faded"
              color="slate"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_AUDIO.RECORD')"
              :disabled="audioState === 'uploading'"
              @click="startRecording"
            />
            <Button
              v-else
              type="button"
              color="slate"
              :label="
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_AUDIO.STOP_RECORDING')
              "
              @click="stopRecording"
            />
            <Button
              type="button"
              variant="faded"
              color="slate"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_AUDIO.IMPORT')"
              :disabled="
                audioState === 'recording' || audioState === 'uploading'
              "
              @click="audioFileInput?.click()"
            />
            <Button
              v-if="audioPreviewUrl"
              type="button"
              variant="faded"
              color="slate"
              :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_AUDIO.REMOVE')"
              :disabled="
                audioState === 'recording' || audioState === 'uploading'
              "
              @click="removeCallAudio"
            />
          </div>
          <p v-if="audioState === 'recording'" class="text-xs text-n-amber-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_AUDIO.RECORDING') }}
          </p>
          <p v-if="audioState === 'uploading'" class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_AUDIO.UPLOADING') }}
          </p>
          <audio
            v-if="audioPreviewUrl"
            :src="audioPreviewUrl"
            controls
            class="w-full"
          />
          <p v-if="formErrors.callAudio" class="text-xs text-n-ruby-11">
            {{ formErrors.callAudio }}
          </p>
        </div>
        <div
          class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
        >
          <div>
            <p class="text-sm font-medium text-n-slate-12">
              {{
                t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_STATUS_MESSAGES.TITLE')
              }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.CALL_STATUS_MESSAGES.INFO') }}
            </p>
          </div>
          <TextArea
            v-for="option in callStatusMessageOptions"
            :id="`call-status-message-${option.key}`"
            :key="option.key"
            v-model="state.callStatusMessages[option.key]"
            :label="option.label"
            :placeholder="option.placeholder"
            :max-length="CALL_STATUS_MESSAGE_MAX_LENGTH"
            show-character-count
            :message="formErrors.callStatusMessages[option.key]"
            :message-type="
              formErrors.callStatusMessages[option.key] ? 'error' : 'info'
            "
          />
        </div>
      </template>
      <template v-else>
        <CampaignAiAgentFields
          v-model:enabled="state.aiAgentEnabled"
          v-model:prompt="state.aiPrompt"
          :preview-context="aiPreviewContext"
          :prompt-error="formErrors.aiPrompt"
        />

        <TextArea
          v-if="!state.aiAgentEnabled"
          v-model="state.message"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.MESSAGE.LABEL')"
          :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.MESSAGE.PLACEHOLDER')"
          show-character-count
          :message="formErrors.message"
          :message-type="formErrors.message ? 'error' : 'info'"
        />
      </template>
    </template>

    <div
      class="flex flex-col gap-3 p-3 border rounded-lg border-n-weak bg-n-alpha-1"
    >
      <div>
        <p class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TITLE') }}
        </p>
        <p class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.INFO') }}
        </p>
      </div>

      <label class="flex items-center gap-2 text-sm text-n-slate-12">
        <input
          v-model="state.dateVariationEnabled"
          type="checkbox"
          class="size-4 accent-n-blue-9"
        />
        <span>
          {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.ENABLED') }}
        </span>
      </label>

      <template v-if="state.dateVariationEnabled">
        <div class="flex flex-col gap-1">
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.DAYS.LABEL') }}
          </p>
          <div class="flex flex-wrap gap-2">
            <button
              v-for="day in dateVariationDayOptions"
              :key="day.value"
              type="button"
              class="px-3 py-1.5 text-sm border rounded-full transition-colors"
              :class="
                state.dateVariationDays.includes(day.value)
                  ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                  : 'border-n-weak text-n-slate-11 hover:bg-n-alpha-1'
              "
              @click="toggleDateVariationDay(day.value)"
            >
              {{ day.label }}
            </button>
          </div>
          <p v-if="formErrors.dateVariationDays" class="text-xs text-n-ruby-11">
            {{ formErrors.dateVariationDays }}
          </p>
        </div>

        <Input
          v-model="state.dateVariationTime"
          :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TIME.LABEL')"
          type="time"
          :message="formErrors.dateVariationTime"
          :message-type="formErrors.dateVariationTime ? 'error' : 'info'"
        />

        <p v-if="nextDateVariationPreview" class="text-xs text-n-slate-11">
          {{
            t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.PREVIEW', {
              value: nextDateVariationPreview,
            })
          }}
        </p>

        <div class="flex flex-col gap-1">
          <p class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TOKENS.LABEL') }}
          </p>
          <p class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.DATE_VARIATION.TOKENS.INFO') }}
          </p>
          <div class="flex flex-col gap-2">
            <div
              v-for="token in dateVariationTokens"
              :key="token.value"
              class="flex flex-col gap-1 p-2 rounded-md bg-n-alpha-black2"
            >
              <span class="text-xs text-n-slate-11">{{ token.label }}</span>
              <code class="text-xs text-n-slate-12">{{ token.value }}</code>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div class="flex flex-col gap-1">
      <label for="audience" class="mb-0.5 text-sm font-medium text-n-slate-12">
        {{ t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL') }}
      </label>
      <TagMultiSelectComboBox
        v-model="state.selectedAudience"
        :options="audienceList"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.LABEL')"
        :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.AUDIENCE.PLACEHOLDER')"
        :has-error="!!formErrors.audience"
        :message="formErrors.audience"
        class="[&>div>button]:bg-n-alpha-black2"
      />
    </div>

    <Input
      v-model="state.scheduledAt"
      :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.LABEL')"
      type="datetime-local"
      :min="currentDateTime"
      :placeholder="t('CAMPAIGN.WHATSAPP.CREATE.FORM.SCHEDULED_AT.PLACEHOLDER')"
      :message="formErrors.scheduledAt"
      :message-type="formErrors.scheduledAt ? 'error' : 'info'"
    />

    <div class="grid w-full grid-cols-1 gap-3 sm:grid-cols-3">
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.BACK')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleBack"
      />
      <Button
        variant="faded"
        color="slate"
        type="button"
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CANCEL')"
        class="w-full bg-n-alpha-2 text-n-blue-11 hover:bg-n-alpha-3"
        @click="handleCancel"
      />
      <Button
        :label="t('CAMPAIGN.WHATSAPP.CREATE.FORM.BUTTONS.CREATE')"
        class="w-full"
        type="submit"
        :is-loading="isCreating"
        :disabled="isCreating || isSubmitDisabled"
      />
    </div>
  </form>
</template>
